import React from 'react';

type SelectionTransform = (selected: string) => {
  text: string;
  selectionStart: number;
  selectionEnd: number;
};

/**
 * Selection-aware editing helpers for a plain `<textarea>` bound to a controlled
 * markdown string: replace the current selection, wrap it in a marker, or turn
 * it into a link. Caret/selection is restored after each edit.
 */
export function useMarkdownFieldActions(value: string, onChange: (value: string) => void) {
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  const replaceSelection = React.useCallback(
    (transform: SelectionTransform) => {
      const el = inputRef.current;
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? value.length;
      const { text, selectionStart, selectionEnd } = transform(value.slice(start, end));
      onChange(value.slice(0, start) + text + value.slice(end));
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(start + selectionStart, start + selectionEnd);
      });
    },
    [value, onChange],
  );

  const wrap = React.useCallback(
    (marker: string) =>
      replaceSelection((selected) => {
        const inner = selected || 'text';
        return {
          text: `${marker}${inner}${marker}`,
          selectionStart: marker.length,
          selectionEnd: marker.length + inner.length,
        };
      }),
    [replaceSelection],
  );

  const insertLink = React.useCallback(
    () =>
      replaceSelection((selected) => {
        const label = selected || 'link text';
        const urlPos = `[${label}](`.length; // caret lands inside the empty URL parens
        return { text: `[${label}](https://)`, selectionStart: urlPos, selectionEnd: urlPos };
      }),
    [replaceSelection],
  );

  return { inputRef, replaceSelection, wrap, insertLink };
}
